import React, { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { BookMarked, Layers, ListChecks } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Page,
  Select,
  TextField,
  type DataTableColumn,
} from '@/components/ui'
import { usePermissions } from '@/hooks/usePermissions'
import {
  useAdoptions,
  useBulkUpdateTopics,
  useCreateAdoption,
  useSyllabusDashboard,
  useSyllabusMatrix,
  transitionSyllabus,
  type SyllabusTopicRow,
} from '@/services/acdmcSyllabusService'
import { errorMessage } from '@/components/acdmc/AcdmcUi'
import '@/components/acdmc/acdmc.css'

const INCLUSION_OPTIONS = [
  { value: 'compulsory', label: 'Compulsory' },
  { value: 'optional', label: 'Optional' },
  { value: 'excluded', label: 'Excluded' },
  { value: 'deferred', label: 'Deferred' },
  { value: 'school_added', label: 'School added' },
]

const AcdmcSchoolSyllabus: React.FC = () => {
  const { can } = usePermissions()
  const canWrite = can('acdmc.curriculum.write')
  const { data: dashboard } = useSyllabusDashboard()
  const { data: adoptions = [] } = useAdoptions()
  const [selectedVersionId, setSelectedVersionId] = useState<string>('')
  const { data: matrix, isLoading: matrixLoading } = useSyllabusMatrix(selectedVersionId || null)
  const createAdoption = useCreateAdoption()
  const bulkUpdate = useBulkUpdateTopics(selectedVersionId)

  const [adoptForm, setAdoptForm] = useState({
    boardId: '',
    frameworkId: '',
    versionId: '',
    title: '',
  })

  const matrixColumns = useMemo<DataTableColumn<SyllabusTopicRow>[]>(
    () => [
      { key: 'unit', header: 'Unit / topic', mobile: 'title', render: (row) => row.title },
      {
        key: 'inclusion',
        header: 'Inclusion',
        render: (row) => (
          canWrite ? (
            <Select
              value={row.inclusionStatus}
              onChange={(e) => saveTopicField(row, { inclusionStatus: e.target.value })}
              options={INCLUSION_OPTIONS}
              aria-label={`Inclusion for ${row.title}`}
            />
          ) : (
            <Badge tone={row.inclusionStatus === 'excluded' ? 'warning' : 'neutral'}>{row.inclusionStatus}</Badge>
          )
        ),
      },
      {
        key: 'periods',
        header: 'Periods',
        numeric: true,
        align: 'right',
        render: (row) =>
          canWrite ? (
            <TextField
              type="number"
              value={row.estimatedPeriods ?? ''}
              onChange={(e) =>
                saveTopicField(row, { estimatedPeriods: e.target.value ? Number(e.target.value) : null })
              }
              aria-label={`Periods for ${row.title}`}
            />
          ) : (
            row.estimatedPeriods ?? '—'
          ),
      },
      {
        key: 'coverage',
        header: 'Coverage',
        render: (row) => row.coverageLevel || 'none',
      },
      { key: 'remarks', header: 'Remarks', render: (row) => row.remarks || '—' },
    ],
    [canWrite, selectedVersionId],
  )

  const saveTopicField = (row: SyllabusTopicRow, patch: Partial<SyllabusTopicRow>) => {
    if (!selectedVersionId) return
    bulkUpdate
      .mutateAsync([{ id: row._id, ...patch }])
      .catch((err) => toast.error(errorMessage(err, 'Could not save topic')))
  }

  const handleCreateAdoption = async () => {
    if (!adoptForm.boardId || !adoptForm.frameworkId || !adoptForm.versionId) {
      toast.error('Enter board, framework and version IDs from the central catalogue.')
      return
    }
    try {
      await createAdoption.mutateAsync(adoptForm)
      toast.success('Draft adoption created for this session')
      setAdoptForm({ boardId: '', frameworkId: '', versionId: '', title: '' })
    } catch (err) {
      toast.error(errorMessage(err, 'Could not create adoption'))
    }
  }

  React.useEffect(() => {
    if (matrix?.version?._id && !selectedVersionId) setSelectedVersionId(matrix.version._id)
  }, [matrix, selectedVersionId])

  return (
    <Page
      title="School syllabus"
      description="Adopt a published curriculum version for this session, configure subjects, map textbooks, and publish school-owned syllabi without changing the central master."
    >
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Session</div>
          <div className="text-lg font-semibold">{dashboard?.academicSession || '—'}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Adoptions</div>
          <div className="text-lg font-semibold">{dashboard?.adoptions ?? 0}</div>
          <div className="text-xs text-muted-foreground">{dashboard?.publishedAdoptions ?? 0} published</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Subjects</div>
          <div className="text-lg font-semibold">{dashboard?.subjectsConfigured ?? 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Pending approval</div>
          <div className="text-lg font-semibold">
            {(dashboard?.pendingAdoptions ?? 0) + (dashboard?.pendingSyllabi ?? 0)}
          </div>
        </Card>
      </div>

      {canWrite && (
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2 font-medium">
            <Layers className="h-4 w-4" />
            New session adoption
          </div>
          <p className="text-sm text-muted-foreground">
            Use IDs from the platform curriculum catalogue (published version only). Central curriculum is never edited here.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <TextField label="Board ID" value={adoptForm.boardId} onChange={(e) => setAdoptForm({ ...adoptForm, boardId: e.target.value })} />
            <TextField
              label="Framework ID"
              value={adoptForm.frameworkId}
              onChange={(e) => setAdoptForm({ ...adoptForm, frameworkId: e.target.value })}
            />
            <TextField
              label="Published version ID"
              value={adoptForm.versionId}
              onChange={(e) => setAdoptForm({ ...adoptForm, versionId: e.target.value })}
            />
            <TextField label="Title" value={adoptForm.title} onChange={(e) => setAdoptForm({ ...adoptForm, title: e.target.value })} />
          </div>
          <Button onClick={handleCreateAdoption} loading={createAdoption.isPending}>
            Create draft adoption
          </Button>
        </Card>
      )}

      <Card className="p-4">
        <div className="flex items-center gap-2 font-medium mb-2">
          <BookMarked className="h-4 w-4" />
          Session adoptions
        </div>
        {adoptions.length === 0 ? (
          <EmptyState title="No adoptions yet" body="Create a draft adoption to link this session to a published curriculum version." />
        ) : (
          <ul className="space-y-2">
            {adoptions.map((a) => (
              <li key={a._id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2">
                <div>
                  <div className="font-medium">{a.title || a.versionCode}</div>
                  <div className="text-xs text-muted-foreground">
                    {a.versionCode} · {a.status}
                  </div>
                </div>
                <Badge tone={a.status === 'published' ? 'success' : 'neutral'}>{a.status}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 font-medium">
          <ListChecks className="h-4 w-4" />
          Syllabus matrix
        </div>
        <TextField
          label="Syllabus version ID"
          hint="Paste the active syllabus version ID for a subject to edit topics."
          value={selectedVersionId}
          onChange={(e) => setSelectedVersionId(e.target.value)}
        />
        {selectedVersionId && matrixLoading && <p className="text-sm text-muted-foreground">Loading matrix…</p>}
        {matrix && (
          <>
            <p className="text-sm text-muted-foreground">
              {matrix.subject.subjectName} · {matrix.subject.capabbleClassKey} · revision {matrix.version.revisionNumber}{' '}
              · {matrix.version.status}
            </p>
            <DataTable columns={matrixColumns} rows={matrix.topics} getRowKey={(r) => r._id} empty={<EmptyState title="No topics yet" body="Topics appear here once the curriculum version has them." compact />} />
            {canWrite && matrix.version.status === 'draft' && (
              <Button
                variant="secondary"
                onClick={() =>
                  transitionSyllabus(matrix.version._id, 'submitted')
                    .then(() => toast.success('Submitted for review'))
                    .catch((err) => toast.error(errorMessage(err, 'Submit failed')))
                }
              >
                Submit for review
              </Button>
            )}
          </>
        )}
      </Card>
    </Page>
  )
}

export default AcdmcSchoolSyllabus
